<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useAuth } from "@/utils/authentication";

import hopeAvatarSrc from "@/assets/hope/hope-with-background.png";
import ButtonAtom from "@/common/atoms/ButtonAtom.vue";
import IconAtom from "@/common/atoms/IconAtom.vue";
import InputAtom from "@/common/atoms/InputAtom.vue";
import ChatRow from "@/components/ChatRow.vue";
import { useStore } from "@/stores";
import { MAX_MESSAGE_CHARS } from "@/stores/help_center_chat";
import { tourAnchorRef } from "@/utils/tour_anchors";
import { measureChatPaint } from "@/utils/chat_stream";

const store = useStore();
const { user } = useAuth();

const isOpen = computed(() => store.state.helpCenterChat?.isOpen ?? false);
const messages = computed(() => store.state.helpCenterChat?.messages ?? []);
const draftAnswer = computed(() => store.state.helpCenterChat?.draft ?? "");
onBeforeUnmount(() => store.dispatch("helpCenterChat/close"));
const pending = computed(() => store.state.helpCenterChat?.pending ?? false);
const error = computed(() => store.state.helpCenterChat?.error ?? "none");
const agentName = computed(() => store.getters["subscription/agentName"]);
// A direct /docs load can open this panel before the subscription hydrates. The agentName getter
// falls back to the store's default name, so guard on the raw state and show a generic label until
// it loads — never surface a configured/default agent name to a session that hasn't fetched one.
const isSubscriptionLoaded = computed(
  () => store.state.subscription?.subscription !== undefined,
);
const displayAgentName = computed(() =>
  isSubscriptionLoaded.value ? agentName.value : "AI Assistant",
);

const draft = ref("");
const messagesRef = ref<HTMLElement | null>(null);
const composerInputRef = ref<InstanceType<typeof InputAtom> | null>(null);
const askComposerAnchorRef = tourAnchorRef("ask-composer");
// Trimmed length: send() and the store trim before checking, so trailing whitespace mustn't trip the limit.
const overLimitBy = computed(
  () => draft.value.trim().length - MAX_MESSAGE_CHARS,
);
const isOverLimit = computed(() => overLimitBy.value > 0);

const close = () => {
  store.dispatch("helpCenterChat/close");
};

const scrollToBottom = async () => {
  await nextTick();
  messagesRef.value?.scrollTo({
    top: messagesRef.value.scrollHeight,
    behavior: "smooth",
  });
};

const send = async () => {
  const text = draft.value.trim();
  if (!text || pending.value || isOverLimit.value) return;
  draft.value = "";
  await store.dispatch("helpCenterChat/ask", text);
};

const retry = async () => {
  await store.dispatch("helpCenterChat/retry");
};

watch([messages, pending, draftAnswer], () => {
  scrollToBottom();
});

let requestStarted = 0;
let firstTextMeasured = false;
watch(pending, (waiting) => {
  if (waiting) {
    requestStarted = performance.now();
    firstTextMeasured = false;
  } else if (firstTextMeasured && error.value === "none" && isOpen.value) {
    measureChatPaint("chat-ui-completion", requestStarted);
  }
});
watch(draftAnswer, (text) => {
  if (text.trim() && !firstTextMeasured && isOpen.value) {
    firstTextMeasured = true;
    measureChatPaint("chat-first-visible-text", requestStarted);
  }
});

watch(isOpen, async (open) => {
  if (!open) return;
  await scrollToBottom();
  // InputAtom doesn't forward a ref to its native <input>; $el is InputAtom's own root.
  composerInputRef.value?.$el?.querySelector("input")?.focus();
});

const errorMessage = computed(() => {
  if (error.value === "rate_limited") {
    return "Too many questions at once. Please wait a moment and try again.";
  }
  if (error.value === "failed") {
    return "Something went wrong answering that. Please try again.";
  }
  return "";
});
</script>

<template>
  <aside v-if="isOpen" class="help-center-ask-panel">
    <div class="help-center-ask-panel__header">
      <div class="help-center-ask-panel__title">
        <img
          :src="hopeAvatarSrc"
          alt=""
          class="help-center-ask-panel__avatar"
        />
        <span>Ask {{ displayAgentName }}</span>
      </div>
      <button
        type="button"
        class="help-center-ask-panel__close"
        aria-label="Close"
        @click="close"
      >
        <IconAtom name="close" :size="20" />
      </button>
    </div>

    <div ref="messagesRef" class="help-center-ask-panel__messages">
      <p v-if="messages.length === 0" class="help-center-ask-panel__empty">
        Ask {{ displayAgentName }} how to do something in Blucor. Answers link
        back to the exact Help Center page they came from.
      </p>

      <ChatRow
        v-for="(message, index) in messages"
        :key="index"
        :message="message.message"
        :is-human="message.isHuman"
        :agent-name="displayAgentName"
        :photoURL="message.isHuman ? user?.picture : undefined"
        :user-display-name="
          message.isHuman ? user?.name || user?.email : undefined
        "
        :sources="message.sources"
      />

      <ChatRow
        v-if="pending && draftAnswer"
        :message="draftAnswer"
        :is-human="false"
        :agent-name="displayAgentName"
      />
      <div
        v-if="pending && !draftAnswer"
        class="help-center-ask-panel__thinking"
      >
        <span class="help-center-ask-panel__spinner" aria-hidden="true" />
        <span>{{ displayAgentName }} is thinking…</span>
      </div>
    </div>

    <div v-if="error !== 'none'" class="help-center-ask-panel__error">
      <span>{{ errorMessage }}</span>
      <button type="button" class="help-center-ask-panel__retry" @click="retry">
        Retry
      </button>
    </div>

    <p v-if="isOverLimit" class="help-center-ask-panel__char-hint">
      {{ overLimitBy }} character{{ overLimitBy === 1 ? "" : "s" }} over the
      {{ MAX_MESSAGE_CHARS }}-character limit.
    </p>

    <form
      :ref="askComposerAnchorRef"
      class="help-center-ask-panel__composer"
      @submit.prevent="send"
    >
      <InputAtom
        ref="composerInputRef"
        v-model="draft"
        :placeholder="`Ask ${displayAgentName} anything`"
        :disabled="pending"
      />
      <ButtonAtom
        type="submit"
        variant="primary"
        size="sm"
        icon-leading="send"
        :disabled="pending || !draft.trim() || isOverLimit"
        aria-label="Send"
      />
    </form>
  </aside>
</template>

<style scoped lang="scss">
@use "@/assets/styles/design-tokens" as *;

.help-center-ask-panel {
  position: fixed;
  top: $spacing-4;
  right: $spacing-4;
  bottom: $spacing-4;
  // Engineering override: no width token exists in design-tokens.scss; matches other fixed-width panels in the app.
  width: 380px;
  max-width: calc(100vw - #{$spacing-6});
  display: flex;
  flex-direction: column;
  background-color: $color-surface-default;
  border: 1px solid $color-border-subtle;
  border-radius: $radius-lg;
  // Engineering override: no box-shadow token exists in design-tokens.scss.
  box-shadow: 0 12px 32px rgba(18, 24, 38, 0.16);
  // Must sit above the Help Center header (sticky, z-index 1030 in help-center.scss); the panel can
  // now be opened from within /docs, and at top: $spacing-4 its title/close would otherwise hide under it.
  z-index: 1060;
  overflow: hidden;
}

.help-center-ask-panel__header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $spacing-3 $spacing-4;
  border-bottom: 1px solid $color-border-subtle;
}

.help-center-ask-panel__title {
  display: flex;
  align-items: center;
  gap: $spacing-2;
  color: $color-content-default;
  @include txt-small-plus;
}

.help-center-ask-panel__avatar {
  width: $spacing-5;
  height: $spacing-5;
  border-radius: $radius-full;
  object-fit: cover;
}

.help-center-ask-panel__close {
  display: flex;
  align-items: center;
  justify-content: center;
  // Engineering override: no size token exists in design-tokens.scss for a fixed icon-button hit area.
  width: 28px;
  height: 28px;
  padding: 0;
  background: none;
  border: none;
  border-radius: $radius-sm;
  color: $color-content-subtle;
  cursor: pointer;

  &:hover {
    background-color: $color-surface-background;
  }
}

.help-center-ask-panel__messages {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: $spacing-4;
  display: flex;
  flex-direction: column;
  gap: $spacing-4;
}

.help-center-ask-panel__empty {
  margin: 0;
  color: $color-content-subtle;
  @include txt-small;
}

.help-center-ask-panel__thinking {
  display: flex;
  align-items: center;
  gap: $spacing-2;
  color: $color-content-subtle;
  @include txt-small;
}

.help-center-ask-panel__spinner {
  display: inline-block;
  // Engineering override: no size token exists in design-tokens.scss for a small inline spinner.
  width: 14px;
  height: 14px;
  border: 2px solid $color-content-primary;
  border-right-color: transparent;
  border-radius: $radius-full;
  animation: help-center-ask-panel-spin 0.75s linear infinite;
}

@keyframes help-center-ask-panel-spin {
  to {
    transform: rotate(360deg);
  }
}

.help-center-ask-panel__error {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $spacing-2;
  margin: 0 $spacing-4 $spacing-3;
  color: $color-red-60;
  @include txt-xsmall;
}

.help-center-ask-panel__retry {
  flex-shrink: 0;
  padding: 0;
  background: none;
  border: none;
  color: $color-red-60;
  font-weight: $txt-xsmall-plus-weight;
  text-decoration: underline;
  cursor: pointer;
}

.help-center-ask-panel__char-hint {
  flex-shrink: 0;
  margin: 0 $spacing-4 $spacing-3;
  color: $color-red-60;
  @include txt-xsmall;
}

.help-center-ask-panel__composer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: $spacing-2;
  padding: $spacing-3 $spacing-4;
  border-top: 1px solid $color-border-subtle;

  :deep(.input-atom) {
    flex: 1 1 auto;
  }
}
</style>
